import { Box, Stack, Typography, useTheme, styled } from "@mui/material";
import {
  Grid24Px,
  R5Robot24Px,
  Workstation24Px,
  basicColours,
  mainColours
} from "@qubit/autoparts";
import { BouncingDotsLoader } from "@qubit/autoparts/src/robotLoader/BouncingDotsLoader";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BinAnimationCategory } from "./BinState";
import { BinsAbove } from "./BinsAbove";

const bouncingDotsStyle = {
  backgroundColor: "white",
  width: ".35em",
  height: ".35em",
  margin: ".2em"
};

const activeColor = basicColours.yellow[300];
const otherPortColor = mainColours.label[5];

const getIconProps = (isActive: boolean) => ({
  fill: isActive ? activeColor : "white",
  height: "40px",
  width: "40px"
});

const getTextProps = (isActive: boolean) => ({
  color: isActive ? activeColor : "white",
  fontSize: "1rem",
  fontWeight: 500,
  lineHeight: "1.2",
  textAlign: "center" as const
});

const BinStatusColumn = styled(Box, {
  shouldForwardProp: (propName) =>
    !["binsAbove", "hasExtraRow"].includes(propName as string)
})<{ hasExtraRow?: boolean }>(
  ({ hasExtraRow = false, theme: { spacing } }) => ({
    display: "grid",
    gridTemplateRows: hasExtraRow ? "1fr auto 1fr" : "auto",
    gap: spacing(0.5),
    alignItems: hasExtraRow ? "stretch" : "center",
    justifyItems: "center",
    justifyContent: "center",
    marginTop: hasExtraRow ? spacing(1.75) : 0,
    minHeight: hasExtraRow ? "120px" : "80px",
    height: "auto"
  })
);

export const BinAnimationOverlay = ({
  animationCategory,
  parentWidth,
  binsAbove
}: {
  animationCategory: BinAnimationCategory;
  parentWidth?: number;
  binsAbove: number | undefined;
}) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const showOneIcon = parentWidth && parentWidth < 270;

  const inGrid = animationCategory === "In Grid";
  const onRobot = animationCategory === "On Robot";
  const inPort = animationCategory === "In Port";
  const otherPort = animationCategory === "Other Port";

  const shouldShowBinsAbove = !!binsAbove && binsAbove > 0 && inGrid;
  const hasExtraContent = shouldShowBinsAbove;

  const SingleIconAnimation = useMemo(() => {
    switch (animationCategory) {
      case "In Grid":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <Grid24Px {...getIconProps(inGrid)} />
              <Typography {...getTextProps(inGrid)}>{t("in grid")}</Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
      case "On Robot":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <R5Robot24Px {...getIconProps(onRobot)} />
              <Typography {...getTextProps(onRobot)}>
                {t("on robot")}
              </Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
      case "In Port":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <Workstation24Px {...getIconProps(inPort)} />
              <Typography {...getTextProps(inPort)}>{t("in port")}</Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
      case "Other Port":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <Workstation24Px
                {...getIconProps(otherPort)}
                fill={otherPortColor}
              />
              <Typography {...getTextProps(otherPort)} color={otherPortColor}>
                {t("at another port")}
              </Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
    }
  }, [animationCategory, inGrid, onRobot, inPort, otherPort, t]);

  const MultiIconAnimation = (
    <>
      {otherPort ? (
        <BinStatusColumn mt={1.75}>
          <Workstation24Px {...getIconProps(otherPort)} fill={otherPortColor} />
          <Typography {...getTextProps(otherPort)} color={otherPortColor}>
            {t("at another port")}
          </Typography>
        </BinStatusColumn>
      ) : (
        <BinStatusColumn hasExtraRow={hasExtraContent}>
          <Grid24Px {...getIconProps(inGrid)} />
          <Typography {...getTextProps(inGrid)}>{t("in grid")}</Typography>
          {shouldShowBinsAbove ? <BinsAbove binsAbove={binsAbove} /> : null}
        </BinStatusColumn>
      )}

      <BouncingDotsLoader
        dotStyle={bouncingDotsStyle}
        isLoading={inGrid || otherPort}
      />

      <BinStatusColumn hasExtraRow={hasExtraContent}>
        <R5Robot24Px {...getIconProps(onRobot)} />
        <Typography {...getTextProps(onRobot)}>{t("on robot")}</Typography>
      </BinStatusColumn>

      <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading={onRobot} />

      <BinStatusColumn hasExtraRow={hasExtraContent}>
        <Workstation24Px {...getIconProps(inPort)} />
        <Typography {...getTextProps(inPort)}>{t("in port")}</Typography>
      </BinStatusColumn>
    </>
  );

  return showOneIcon ? (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      flexGrow={1}
      gap="8px"
      bgcolor={palette.binAnimation.overlay}
    >
      {SingleIconAnimation}
    </Stack>
  ) : (
    <Box
      display="grid"
      gridTemplateColumns="2fr 1fr 2fr 1fr 2fr"
      alignItems="center"
      alignContent="center"
      overflow="hidden"
      flexGrow={1}
      p={2}
      bgcolor={palette.binAnimation.overlay}
    >
      {MultiIconAnimation}
    </Box>
  );
};
